/**
 * Session storage keys and helpers for persisting profile and cart
 * so data survives page navigation and refresh within the same tab.
 */

const PROFILE_KEY = 'profile';
const CART_KEY = 'cart';
const COMBO_KEY = 'combo';

function safeGet<T>(key: string): T | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: unknown): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota or serialization errors
  }
}

/** Load profile from sessionStorage. Rebuilds selfieUrl from selfieBase64 after refresh. */
export function loadProfile(): Record<string, unknown> | null {
  const data = safeGet<Record<string, unknown>>(PROFILE_KEY);
  if (!data) return null;
  // After refresh, blob URLs are invalid; use selfieBase64 to show the selfie again
  const base64 = data.selfieBase64 as string | undefined;
  if (base64 && typeof base64 === 'string') {
    data.selfieUrl = `data:image/jpeg;base64,${base64}`;
  }
  return data;
}

/** Save profile to sessionStorage. Strips blob selfieUrl so we only persist base64. */
export function saveProfile(profile: Record<string, unknown>): void {
  const copy = { ...profile };
  // Don't persist blob URLs; they're invalid after refresh. We keep selfieBase64.
  if (typeof copy.selfieUrl === 'string' && copy.selfieUrl.startsWith('blob:')) {
    copy.selfieUrl = '';
  }
  safeSet(PROFILE_KEY, copy);
}

/** Load cart from sessionStorage. */
export function loadCart(): { items: unknown[]; total: number } | null {
  const data = safeGet<{ items: unknown[]; total: number }>(CART_KEY);
  if (!data || !Array.isArray(data.items)) return null;
  return { items: data.items, total: typeof data.total === 'number' ? data.total : 0 };
}

/** Save cart to sessionStorage. */
export function saveCart(cart: { items: unknown[]; total: number }): void {
  safeSet(CART_KEY, cart);
}

/** Load combo state from sessionStorage. Rebuilds selfieUrl from selfieBase64 after refresh. */
export function loadCombo(): Record<string, unknown> | null {
  const data = safeGet<Record<string, unknown>>(COMBO_KEY);
  if (!data) return null;
  const base64 = data.selfieBase64 as string | undefined;
  if (base64 && typeof base64 === 'string') {
    data.selfieUrl = `data:image/jpeg;base64,${base64}`;
  }
  return data;
}

/** Save combo state to sessionStorage. Strips blob selfieUrl so we only persist base64. */
export function saveCombo(combo: Record<string, unknown>): void {
  const copy = { ...combo };
  if (typeof copy.selfieUrl === 'string' && copy.selfieUrl.startsWith('blob:')) {
    copy.selfieUrl = '';
  }
  safeSet(COMBO_KEY, copy);
}
